import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { Link } from "react-router-dom";
import ExperiencesAPI from "../services/ExperiencesAPI";
import { AppButton } from "./ui";
import { useAuth } from "../context/AuthContext";

const DEFAULT_EXPIRY_HOURS = 24;

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const formatDateTime = (value) => {
  if (!value) {
    return "Unknown";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "Unknown";
  }

  return parsedDate.toLocaleString();
};

const getJoinCodeStatus = (joinCode, now = new Date()) => {
  const persistedStatus = String(joinCode?.status ?? "ACTIVE").trim().toUpperCase();

  if (persistedStatus && persistedStatus !== "ACTIVE") {
    return persistedStatus;
  }

  const expiresAtMs = new Date(joinCode?.expiresAt).getTime();
  if (Number.isFinite(expiresAtMs) && expiresAtMs <= now.getTime()) {
    return "EXPIRED";
  }

  return "ACTIVE";
};

const getJoinCodeStatusColor = (status) => {
  if (status === "ACTIVE") {
    return "success";
  }

  if (status === "EXPIRED") {
    return "warning";
  }

  return "default";
};

const sortJoinCodes = (joinCodes) =>
  [...joinCodes].sort((left, right) => {
    const leftCreatedAt = new Date(left?.createdAt || 0).getTime();
    const rightCreatedAt = new Date(right?.createdAt || 0).getTime();
    return rightCreatedAt - leftCreatedAt;
  });

const JoinCodeDialog = ({ open, onClose, experienceId, experienceName }) => {
  const { hasClassroomAccess } = useAuth();
  const [joinCodes, setJoinCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const loadJoinCodes = useCallback(async () => {
    if (!experienceId) {
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await ExperiencesAPI.get(
        `experiences/${experienceId}/join-codes`,
        { requiresAuth: true },
      );
      setJoinCodes(sortJoinCodes(getApiResponseData(response) || []));
    } catch (error) {
      console.error("Failed to load join codes", error);
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to load join codes for this experience.",
      );
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    if (!open || !experienceId || !hasClassroomAccess) {
      return;
    }

    loadJoinCodes();
  }, [experienceId, hasClassroomAccess, loadJoinCodes, open]);

  useEffect(() => {
    if (!open) {
      setErrorMessage("");
      setSuccessMessage("");
    }
  }, [open]);

  const displayedJoinCodes = useMemo(
    () =>
      joinCodes.map((joinCode) => ({
        ...joinCode,
        status: getJoinCodeStatus(joinCode),
      })),
    [joinCodes],
  );

  const activeJoinCode = useMemo(
    () => displayedJoinCodes.find((joinCode) => joinCode.status === "ACTIVE") || null,
    [displayedJoinCodes],
  );

  const handleCreateJoinCode = async () => {
    if (!experienceId) {
      return;
    }

    if (!hasClassroomAccess) {
      setErrorMessage("Upgrade required before you can generate join codes.");
      return;
    }

    setCreating(true);
    setErrorMessage("");

    try {
      const response = await ExperiencesAPI.post(
        `experiences/${experienceId}/join-codes`,
        { expiresInHours: DEFAULT_EXPIRY_HOURS },
        { requiresAuth: true },
      );
      const createdJoinCode = getApiResponseData(response);

      setJoinCodes((currentJoinCodes) =>
        sortJoinCodes([
          createdJoinCode,
          ...currentJoinCodes.filter(
            (existingJoinCode) => existingJoinCode.id !== createdJoinCode.id,
          ),
        ]),
      );
      setSuccessMessage("Join code created.");
    } catch (error) {
      console.error("Failed to create join code", error);
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to create a join code right now.",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeJoinCode = async (joinCodeId) => {
    if (!joinCodeId) {
      return;
    }

    if (!hasClassroomAccess) {
      return;
    }

    setRevokingId(joinCodeId);
    setErrorMessage("");

    try {
      const response = await ExperiencesAPI.post(
        `join-codes/${joinCodeId}/revoke`,
        {},
        { requiresAuth: true },
      );
      const revokedJoinCode = getApiResponseData(response);

      setJoinCodes((currentJoinCodes) =>
        sortJoinCodes(
          currentJoinCodes.map((joinCode) =>
            joinCode.id === revokedJoinCode.id ? revokedJoinCode : joinCode,
          ),
        ),
      );
      setSuccessMessage("Join code revoked.");
    } catch (error) {
      console.error("Failed to revoke join code", error);
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to revoke this join code right now.",
      );
    } finally {
      setRevokingId("");
    }
  };

  const handleCopyValue = async (label, value) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setSuccessMessage(`${label} copied.`);
    } catch (error) {
      console.error(`Failed to copy ${label}`, error);
      setErrorMessage(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={creating || Boolean(revokingId) ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Join Codes</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography color="text.secondary">
            Generate a 24-hour join code for {experienceName || "this experience"}.
            Students can use either the short code or the join URL.
          </Typography>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}
          {!hasClassroomAccess && (
            <Alert
              severity="warning"
              action={
                <AppButton component={Link} to="/upgrade" kind="secondary" onClick={onClose}>
                  Upgrade
                </AppButton>
              }
            >
              Join codes are available during trial and with an active classroom license.
            </Alert>
          )}

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.72 }}>
                    Current Active Code
                  </Typography>
                  <Typography variant="h4" sx={{ letterSpacing: "0.18em" }}>
                    {activeJoinCode?.code || "None"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <AppButton
                    kind="secondary"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={handleCreateJoinCode}
                    disabled={creating || !hasClassroomAccess}
                  >
                    {creating ? "Generating..." : activeJoinCode ? "Generate New" : "Generate"}
                  </AppButton>
                </Stack>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Expires: {activeJoinCode ? formatDateTime(activeJoinCode.expiresAt) : "No active code"}
              </Typography>

              {activeJoinCode?.joinUrl && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <AppButton
                    kind="secondary"
                    startIcon={<ContentCopyOutlinedIcon />}
                    onClick={() => handleCopyValue("Join code", activeJoinCode.code)}
                  >
                    Copy Code
                  </AppButton>
                  <AppButton
                    kind="secondary"
                    startIcon={<LinkOutlinedIcon />}
                    onClick={() => handleCopyValue("Join link", activeJoinCode.joinUrl)}
                  >
                    Copy Link
                  </AppButton>
                  <AppButton
                    kind="ghost"
                    startIcon={<BlockOutlinedIcon />}
                    onClick={() => handleRevokeJoinCode(activeJoinCode.id)}
                    disabled={revokingId === activeJoinCode.id}
                  >
                    {revokingId === activeJoinCode.id ? "Revoking..." : "Revoke"}
                  </AppButton>
                </Stack>
              )}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="overline" sx={{ opacity: 0.72 }}>
              Recent Codes
            </Typography>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : displayedJoinCodes.length > 0 ? (
              <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                {displayedJoinCodes.map((joinCode) => (
                  <Box
                    key={joinCode.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h6" sx={{ letterSpacing: "0.12em" }}>
                          {joinCode.code}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Chip
                            size="small"
                            label={joinCode.status}
                            color={getJoinCodeStatusColor(joinCode.status)}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Expires {formatDateTime(joinCode.expiresAt)}
                          </Typography>
                        </Stack>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Redeemed {joinCode.redeemCount || 0} times
                      </Typography>
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ mt: 1.5 }}
                    >
                      <AppButton
                        kind="secondary"
                        startIcon={<ContentCopyOutlinedIcon />}
                        onClick={() => handleCopyValue("Join code", joinCode.code)}
                      >
                        Copy Code
                      </AppButton>
                      <AppButton
                        kind="secondary"
                        startIcon={<LinkOutlinedIcon />}
                        onClick={() => handleCopyValue("Join link", joinCode.joinUrl)}
                      >
                        Copy Link
                      </AppButton>
                      {joinCode.status === "ACTIVE" && (
                        <AppButton
                          kind="ghost"
                          startIcon={<BlockOutlinedIcon />}
                          onClick={() => handleRevokeJoinCode(joinCode.id)}
                          disabled={revokingId === joinCode.id}
                        >
                          {revokingId === joinCode.id ? "Revoking..." : "Revoke"}
                        </AppButton>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ mt: 1.5 }} color="text.secondary">
                No join codes generated yet.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <AppButton kind="ghost" onClick={onClose} disabled={creating || Boolean(revokingId)}>
          Close
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default JoinCodeDialog;
